import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { setBlogMeta } from "./blogMeta";
import "./blog.css";

const BACKEND_URL = (process.env.React_APP_BACKEND_URL || "").replace(
  /\/+$/,
  "",
);
const API_URL = `${BACKEND_URL}/api/blog/articles`;

function getInitialData() {
  if (typeof window === "undefined") return null;

  return window.__BLOG_INITIAL_DATA__?.type === "index"
    ? window.__BLOG_INITIAL_DATA__
    : null;
}

function BlogGridPage() {
  const initialDataRef = useRef(getInitialData());
  const initial = initialDataRef.current;
  const initialRequestStarted = useRef(false);
  const latestRequestId = useRef(0);

  const [articles, setArticles] = useState(initial?.articles || []);
  const [pagination, setPagination] = useState(
    initial?.pagination || { current_page: 1, last_page: 1 },
  );
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState("");

  const loadPage = useCallback(async (page = 1, shouldScroll = true) => {
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;

    setLoading(true);
    setError("");

    try {
      const response = await axios.get(API_URL, {
        params: { page, per_page: 12 },
      });

      // Ignore an older response if a newer request finished first.
      if (latestRequestId.current !== requestId) return;

      const payload = response?.data || {};

      setArticles(Array.isArray(payload.data) ? payload.data : []);
      setPagination({
        current_page: Number(payload.current_page) || 1,
        last_page: Number(payload.last_page) || 1,
      });

      if (shouldScroll && typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (requestError) {
      if (latestRequestId.current !== requestId) return;

      const status = requestError?.response?.status;
      const serverMessage = requestError?.response?.data?.message;

      setArticles([]);
      setError(
        serverMessage ||
          (status
            ? `לא ניתן לטעון את המאמרים. שגיאת שרת ${status}.`
            : "לא ניתן להתחבר לשרת המאמרים."),
      );
    } finally {
      if (latestRequestId.current === requestId) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const canonical = `${window.location.origin}/blog`;
    setBlogMeta({
      title: "מאמרים ומדריכים | Bidit",
      description: "מאמרים, מדריכים וטיפים שימושיים מבית Bidit.",
      canonical,
      type: "website",
    });
  }, []);

  useEffect(() => {
    // Laravel can inject initial data when it serves the page. When React serves
    // the route directly, fetch page 1 once so loading always resolves.
    if (!initial && !initialRequestStarted.current) {
      initialRequestStarted.current = true;
      loadPage(1, false);
    }
  }, [initial, loadPage]);

  return (
    <main className="blog-page" dir="rtl">
      <header className="blog-page__header">
        <h1>מאמרים ומדריכים</h1>
        <p>מאמרים, מדריכים וטיפים שימושיים מבית Bidit.</p>
      </header>

      {error && (
        <div className="blog-alert" role="alert">
          <p>{error}</p>
          <button type="button" onClick={() => loadPage(1, false)}>
            נסה שוב
          </button>
        </div>
      )}

      {loading ? (
        <div className="blog-state" aria-live="polite">
          טוען מאמרים...
        </div>
      ) : articles.length === 0 && !error ? (
        <div className="blog-state">עדיין לא פורסמו מאמרים.</div>
      ) : articles.length > 0 ? (
        <section className="blog-grid" aria-label="מאמרים שפורסמו">
          {articles.map((article) => (
            <article className="blog-card" key={article.id}>
              <Link to={`/blog/${article.slug}`}>
                {article.display_image && (
                  <div className="blog-card__media">
                    <img
                      src={article.display_image}
                      alt={article.title}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                )}
                <div className="blog-card__content">
                  {article.published_at && (
                    <time dateTime={article.published_at}>
                      {new Date(article.published_at).toLocaleDateString(
                        "he-IL",
                      )}
                    </time>
                  )}
                  <h2>{article.title}</h2>
                  <p>{article.excerpt}</p>
                </div>
              </Link>
            </article>
          ))}
        </section>
      ) : null}

      {pagination.last_page > 1 && (
        <nav className="blog-pagination" aria-label="עמודי מאמרים">
          <button
            type="button"
            disabled={pagination.current_page <= 1 || loading}
            onClick={() => loadPage(pagination.current_page - 1)}
          >
            הקודם
          </button>
          <span>
            {pagination.current_page} / {pagination.last_page}
          </span>
          <button
            type="button"
            disabled={
              pagination.current_page >= pagination.last_page || loading
            }
            onClick={() => loadPage(pagination.current_page + 1)}
          >
            הבא
          </button>
        </nav>
      )}
    </main>
  );
}

export default BlogGridPage;
